/**
 * Plantillas de impresión avanzadas (PLAN_SUTEX_Y_PLANTILLAS §3.0bis):
 * /api/v1/config/print-templates/. Cada fila es una versión; las de una misma
 * plantilla comparten `family_id`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import apiClient from '@/lib/apiClient'

export type PrintTemplateStatus = 'draft' | 'published' | 'retired'

export interface PrintTemplateSummary {
  id: string
  family_id: string
  version: number
  name: string
  document_types: number[]
  status: PrintTemplateStatus
  created_at: string
  updated_at: string
  published_at: string | null
}

export interface PrintTemplate extends PrintTemplateSummary {
  definition: Record<string, unknown>
  /** Otras publicadas que cubren alguno de los mismos tipos: gana la más reciente. */
  overlapping: { id: string; name: string; document_types: number[] }[]
}

export interface PrintTemplateBase {
  key: string
  name: string
  definition: Record<string, unknown>
}

export type PreviewVariant = 'documento' | 'liquidacion' | 'exportacion' | 'boleta'

const keys = {
  all: ['config', 'print-templates'] as const,
  detail: (id: string) => ['config', 'print-templates', id] as const,
  bases: ['config', 'print-templates', 'bases'] as const,
}

export function usePrintTemplates() {
  return useQuery<PrintTemplateSummary[]>({
    queryKey: keys.all,
    queryFn: async () => (await apiClient.get('/config/print-templates/')).data,
  })
}

export function usePrintTemplate(id: string | null) {
  return useQuery<PrintTemplate>({
    queryKey: keys.detail(id ?? ''),
    queryFn: async () => (await apiClient.get(`/config/print-templates/${id}/`)).data,
    enabled: !!id,
  })
}

export function usePrintTemplateBases() {
  return useQuery<PrintTemplateBase[]>({
    queryKey: keys.bases,
    queryFn: async () => (await apiClient.get('/config/print-templates/bases/')).data,
    staleTime: Infinity,
  })
}

function useInvalidar() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: keys.all })
}

export function useCreatePrintTemplate() {
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: async (data: {
      name: string; document_types: number[]; base?: string; definition?: Record<string, unknown>
    }) =>
      (await apiClient.post('/config/print-templates/', data)).data as PrintTemplate,
    onSuccess: invalidar,
  })
}

export function useUpdatePrintTemplate() {
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: async ({ id, data }: {
      id: string
      data: { name?: string; document_types?: number[]; definition?: Record<string, unknown> }
    }) => (await apiClient.patch(`/config/print-templates/${id}/`, data)).data as PrintTemplate,
    onSuccess: invalidar,
  })
}

export function useDeletePrintTemplate() {
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/config/print-templates/${id}/`) },
    onSuccess: invalidar,
  })
}

export function usePrintTemplateAction() {
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'publish' | 'new-version' | 'retire' }) =>
      (await apiClient.post(`/config/print-templates/${id}/${action}/`)).data as PrintTemplate,
    onSuccess: invalidar,
  })
}

export class PreviewError extends Error {
  constructor(message: string, public details: Record<string, string[]> = {}) {
    super(message)
  }
}

/**
 * PDF sin guardar. La respuesta es un blob, así que un error también llega
 * como blob: se lee el JSON de adentro para mostrar los errores con su ruta.
 */
export type CopiaDeVistaPrevia = 'original' | 'cedible' | 'ambas'

export async function previewPrintTemplate(body: {
  definition: unknown
  variant?: PreviewVariant
  document_id?: string
  copy?: CopiaDeVistaPrevia
}): Promise<{ url: string; bytes: number }> {
  try {
    const res = await apiClient.post('/config/print-templates/preview/', body, { responseType: 'blob' })
    const bytes = Number(res.headers['x-pdf-bytes'] ?? res.data.size)
    return { url: URL.createObjectURL(res.data), bytes }
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
      try {
        const { error } = JSON.parse(await e.response.data.text())
        throw new PreviewError(error?.message || 'No se pudo generar la vista previa.', error?.details || {})
      } catch (parseado) {
        if (parseado instanceof PreviewError) throw parseado
      }
    }
    throw new PreviewError('No se pudo generar la vista previa.')
  }
}

// ─── Editor (F1c) ────────────────────────────────────────────────────────────

export interface NodoDePaleta {
  name: string
  /** Relativa a la raíz del documento; bajo un repetible, relativa a él. */
  path: string
  description: string
  required: boolean
  repeatable: boolean
  children?: NodoDePaleta[]
  values?: { value: string; description: string }[]
  /** Otras variantes que cubre la plantilla donde el campo no existe: ahí se imprime vacío. */
  missing_in?: PreviewVariant[]
}

export function usePrintTemplatePalette(variant: PreviewVariant, documentTypes: number[]) {
  return useQuery<{ variant: PreviewVariant; compared_with: PreviewVariant[]; tree: NodoDePaleta[] }>({
    queryKey: ['config', 'print-templates', 'palette', variant, documentTypes.join(',')],
    queryFn: async () => (await apiClient.get('/config/print-templates/palette/', {
      params: { variant, document_types: documentTypes.join(',') },
    })).data,
    staleTime: Infinity,
  })
}

export interface DatosDelElemento {
  visible: boolean
  text?: string
  rows?: string[][]
  /** Detalle con referencias encima: sus filas, vacío si el ejemplo no trae. */
  references?: string[][]
  asset?: string
  total_rows?: number
  /** Qué columnas se dibujan con el documento de ejemplo (las que tienen condición pueden no ir). */
  columns_visible?: boolean[]
  references_visible?: boolean[]
  error?: string
}

export interface DatosDelLienzo {
  elements: Record<string, DatosDelElemento>
  errors: Record<string, string[]>
}

/** Lo que el lienzo muestra en cada elemento, con los formatos y condiciones del PDF. */
export async function fetchCanvasData(
  definition: unknown, variant: PreviewVariant, copy: 'original' | 'cedible' = 'original',
): Promise<DatosDelLienzo> {
  return (await apiClient.post('/config/print-templates/canvas/', { definition, variant, copy })).data
}

// ─── Biblioteca de imágenes y PDF de fondo (F1d) ─────────────────────────────

export type TipoDeArchivo = 'image' | 'background'

export interface PrintAsset {
  id: string
  kind: TipoDeArchivo
  name: string
  mime: string
  width_px: number | null
  height_px: number | null
  page_count: number | null
  page_width_mm: number | null
  page_height_mm: number | null
  size_bytes: number
  warnings: string[]
  created_at: string
}

const llavesDeArchivos = {
  todos: ['config', 'print-templates', 'assets'] as const,
  binario: (id: string, que: string) => ['config', 'print-templates', 'assets', id, que] as const,
}

export function usePrintAssets(kind: TipoDeArchivo) {
  return useQuery<PrintAsset[]>({
    queryKey: [...llavesDeArchivos.todos, kind],
    queryFn: async () => (await apiClient.get('/config/print-templates/assets/', { params: { kind } })).data,
  })
}

export function useUploadPrintAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, kind }: { file: File; kind: TipoDeArchivo }) => {
      const datos = new FormData()
      datos.append('file', file)
      datos.append('kind', kind)
      datos.append('name', file.name)
      return (await apiClient.post('/config/print-templates/assets/', datos, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data as PrintAsset
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: llavesDeArchivos.todos }),
  })
}

export function useDeletePrintAsset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/config/print-templates/assets/${id}/`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: llavesDeArchivos.todos }),
  })
}

/**
 * La URL local (blob) de una imagen de la biblioteca o de la miniatura de un
 * fondo. El binario pide el token de la sesión, así que no sirve un `src` directo.
 * El contenido de un id no cambia: se guarda en caché sin vencer.
 */
export function useAssetUrl(id: string | undefined, que: 'content' | 'preview') {
  const { data } = useQuery({
    queryKey: llavesDeArchivos.binario(id ?? '', que),
    queryFn: async () => {
      const res = await apiClient.get(`/config/print-templates/assets/${id}/${que}/`, { responseType: 'blob' })
      return URL.createObjectURL(res.data as Blob)
    },
    enabled: !!id,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })
  return data
}
